import React, {
  useCallback,
  createContext,
  useReducer,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getBusinessPageLinks } from "../services/postService";
import authService from "../services/authService";
import {
  clearStoredSession,
  getStoredSession,
  hasStoredSessionToken,
  persistStoredSession,
} from "../shared/authSession";
import UserService from "../services/categoryService";

const SET_USER = "SET_USER";
const LOGOUT_USER = "LOGOUT_USER";

const initialState = {
  token: null,
  refreshToken: null,
  organizationId: null,
  organizationSubdomain: null,
  role: [],
  requiresOrganizationSetup: false,
  isAuthenticated: false,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case SET_USER:
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken ?? null,
        organizationId: action.payload.organizationId ?? null,
        organizationSubdomain: action.payload.organizationSubdomain ?? null,
        role: action.payload.role ?? [],
        requiresOrganizationSetup:
          action.payload.requiresOrganizationSetup ??
          (Boolean(action.payload.token) && action.payload.organizationId == null),
        isAuthenticated: Boolean(action.payload.token),
      };
    case LOGOUT_USER:
      return { ...initialState };
    default:
      return state;
  }
};

const AuthContext = createContext();

const buildBusinessPagesFromLinks = (storedLinks = {}) =>
  Object.entries(storedLinks).reduce((acc, [platform, url]) => {
    acc[platform] = {
      url: url || "",
      username: "",
      tagText: "",
    };
    return acc;
  }, {});

const getLegacyBusinessPages = () => {
  try {
    const storedLinks = JSON.parse(sessionStorage.getItem("businessPageLinks") || "{}");
    return buildBusinessPagesFromLinks(storedLinks);
  } catch (error) {
    return {};
  }
};

const getStoredBusinessPages = () => {
  try {
    const storedBusinessPages = JSON.parse(sessionStorage.getItem("businessPages") || "{}");
    if (storedBusinessPages && Object.keys(storedBusinessPages).length > 0) {
      return storedBusinessPages;
    }
  } catch (error) {
    return {};
  }
  return getLegacyBusinessPages();
};

const extractBusinessPageLinks = (businessPages = {}) =>
  Object.entries(businessPages).reduce((acc, [platform, info]) => {
    acc[platform] = info?.url || "";
    return acc;
  }, {});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [userProfile, setUserProfile] = useState(null);
  const [loyaltyPoints, setLoyaltyPoints] = useState(null);
  const [categoriesData, setCategoriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesDetails, setCategoriesDetails] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [businessPages, setBusinessPages] = useState(() => getStoredBusinessPages());
  const [businessPagesLoading, setBusinessPagesLoading] = useState(false);
  const [businessPagesLoaded, setBusinessPagesLoaded] = useState(
    () => Object.keys(getStoredBusinessPages()).length > 0
  );
  const businessPagesRequestRef = useRef(null);
  useEffect(() => {
    const bootstrap = async () => {
      const isSsoRedirect = typeof window !== "undefined" && window.location.search.includes("sso_auth=success");
      const storedSession = getStoredSession();
      if (storedSession.token && !isSsoRedirect) {
        dispatch({
          type: SET_USER,
          payload: storedSession,
        });
        setLoading(false);
        return;
      }

      // No local session flags — this also happens right after a cross-subdomain
      // redirect, since sessionStorage/localStorage are per-origin and don't carry
      // over even though the httpOnly auth cookie (scoped to the whole root domain)
      // does. Check with the backend before treating this as logged out.
      try {
        const response = await authService.getSession();
        if (response?.data?.status === true) {
          const session = {
            token: "session-present",
            refreshToken: "session-present",
            organizationId: response.data.organizationId ?? null,
            organizationSubdomain: response.data.organizationSubdomain ?? null,
            role: response.data.role ?? [],
            requiresOrganizationSetup: response.data.organizationId == null,
          };
          persistStoredSession(session);
          dispatch({ type: SET_USER, payload: session });

          if (isSsoRedirect) {
            const roles = response.data.role ?? [];
            if (roles.includes("ROLE_ADMIN") && typeof window !== "undefined" && window.location.pathname.startsWith("/employees")) {
              window.location.href = "/admin";
              return;
            }
          }
        }
      } catch (error) {
        // No valid cookie — genuinely logged out, matches today's behavior.
      }
      setLoading(false); // finished checking for token
    };

    bootstrap();
  }, []);

  const refreshBusinessPageLinks = useCallback(async () => {
    if (!hasStoredSessionToken()) {
      setBusinessPages({});
      setBusinessPagesLoaded(false);
      setBusinessPagesLoading(false);
      businessPagesRequestRef.current = null;
      sessionStorage.removeItem("businessPages");
      sessionStorage.removeItem("businessPageLinks");
      return {};
    }

    if (businessPagesRequestRef.current) {
      return businessPagesRequestRef.current;
    }

    setBusinessPagesLoading(true);

    const request = (async () => {
    try {
      const response = await getBusinessPageLinks();
      const normalizedBusinessPages =
        response?.businessPages ||
        buildBusinessPagesFromLinks(response?.businessPageLinks || {});
      setBusinessPages(normalizedBusinessPages);
      setBusinessPagesLoaded(true);
      sessionStorage.setItem("businessPages", JSON.stringify(normalizedBusinessPages));
      sessionStorage.setItem(
        "businessPageLinks",
        JSON.stringify(extractBusinessPageLinks(normalizedBusinessPages))
      );
      return normalizedBusinessPages;
    } catch (error) {
      console.error("Unable to refresh business page links", error);
      setBusinessPagesLoaded(true);
      return {};
    } finally {
      setBusinessPagesLoading(false);
      businessPagesRequestRef.current = null;
    }
    })();

    businessPagesRequestRef.current = request;
    return request;
  }, []);

  useEffect(() => {
    if (state.token) {
      refreshBusinessPageLinks();
    } else {
      setBusinessPages({});
      setBusinessPagesLoaded(false);
      setBusinessPagesLoading(false);
      businessPagesRequestRef.current = null;
      sessionStorage.removeItem("businessPages");
      sessionStorage.removeItem("businessPageLinks");
    }
  }, [state.token, refreshBusinessPageLinks]);

  const refreshLoyaltyPoints = useCallback(async () => {
    try {
      const response = await UserService.getLoyaltyPoints();
      if (response?.loyalty) {
        setLoyaltyPoints(response.loyalty);
      }
      window.dispatchEvent(new CustomEvent("loyaltyPointsUpdated"));
    } catch (error) {
      console.error("Failed to refresh loyalty points:", error);
    }
  }, []);

  const setUser = (sessionOrToken) => {
    persistStoredSession(sessionOrToken);
    if (typeof sessionOrToken === "string") {
      dispatch({ type: SET_USER, payload: { token: sessionOrToken } });
      return;
    }
    dispatch({ type: SET_USER, payload: sessionOrToken || {} });
  };

  const clearUser = () => {
    clearStoredSession();
    dispatch({ type: LOGOUT_USER });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        state,
        loading,
        dispatch,
        setUser,
        clearUser,
        loyaltyPoints,
        setLoyaltyPoints,
        refreshLoyaltyPoints,
        userProfile,
        setUserProfile,
        categoriesData,
        setCategoriesData,
        categoriesDetails,
        setCategoriesDetails,
        searchValue,
        setSearchValue,
        businessPages,
        businessPagesLoading,
        businessPagesLoaded,
        setBusinessPages,
        businessPageLinks: extractBusinessPageLinks(businessPages),
        refreshBusinessPageLinks,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const Auth = () => useContext(AuthContext);
